import { getDuplicateName } from '@doss/common-all'
import { MenuItem, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { IoDuplicateOutline } from 'react-icons/io5'
import { RxPencil2, RxTrash } from 'react-icons/rx'
import {
  GetDetailedTableDocument,
  GetDetailedTableQuery,
  TableColumnFilterInput,
  TableTabSortInput,
  useCreateTableTabMutation,
} from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DetailedTableTab } from '~/components/tables/types'
import { Menu } from '~/components/ui/atoms/menu/Menu'
import { useDossSnackbar } from '../DossSnackbar'
import { DeleteTableTabDialog } from './DeleteTableTabDialog'
import { RenameTableTabDialog } from './RenameTableTabDialog'

interface TableTabMenuProps {
  handleTabMenuClose: () => void
  tableName: string
  orderedTableTabs: DetailedTableTab[]
  selectedTab: DetailedTableTab
  handleTabChange: (tableTab: DetailedTableTab) => void
  tableId: string
  anchorEl: HTMLElement | null
  anchorOrigin?: { vertical: number; horizontal: number }
}

/**
 * Renders action options for a table tab like Rename, Duplicate, Delete
 */
export function TableTabMenu({
  handleTabMenuClose,
  selectedTab,
  orderedTableTabs,
  handleTabChange,
  tableName,
  tableId,
  anchorEl,
  anchorOrigin = { vertical: 40, horizontal: 145 },
}: TableTabMenuProps) {
  const { t } = useTypedTranslation()
  const [tabToDelete, setTabToDelete] = useState<null | DetailedTableTab>(null)
  const [tabToUpdate, setTabToUpdate] = useState<null | DetailedTableTab>(null)

  const snackbar = useDossSnackbar()

  const [createTableTab] = useCreateTableTabMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }
      const queryData: GetDetailedTableQuery | null = cache.readQuery({
        query: GetDetailedTableDocument,
        variables: { tableId },
      })
      // Update the Apollo Cache with the new table tab
      if (queryData) {
        // Check if the tab already exists in the cache
        const tabExists = queryData.table.tableTabs.some(
          (existingTab) => existingTab.id === data.createTableTab.id,
        )

        if (!tabExists) {
          cache.writeQuery({
            query: GetDetailedTableDocument,
            variables: { tableId },
            data: {
              table: {
                ...queryData.table,
                tableTabs: [...queryData.table.tableTabs, data.createTableTab],
              },
            },
          })
        }
      }
    },
  })

  const handleDuplicateTableTabClick = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    // Create a new table using the name of the current tab, next possible order,
    // and columns saved on the current tab (table columns and system-generated columns)
    const columnInputs = selectedTab.columns
      .map((tableTabColumn) => {
        if (tableTabColumn.tableColumn?.id != null) {
          return { tableColumnId: tableTabColumn.tableColumn.id, order: tableTabColumn.order }
        }
        if (tableTabColumn.systemGeneratedColumnType != null) {
          return {
            systemGeneratedColumnType: tableTabColumn.systemGeneratedColumnType,
            order: tableTabColumn.order,
          }
        }
        return null
      })
      .filter((col): col is NonNullable<typeof col> => col != null)

    const newName = getDuplicateName(selectedTab.name)
    // Check if the new name already exists in the list of tabs
    if (orderedTableTabs.some((tab) => tab.name === newName)) {
      snackbar.showError(
        t('table_tab.duplicate.error', {
          newName,
        }),
      )
      return
    }

    // Create sort inputs for the sorts saved on current tab (table-column and system-generated column sorts)
    const sortsInput: TableTabSortInput[] = selectedTab.sorts
      .map((sortItem) => {
        if (sortItem.tableColumn != null) {
          return {
            direction: sortItem.direction,
            tableColumnId: sortItem.tableColumn.id,
          }
        }
        if (sortItem.systemGeneratedColumnType != null) {
          return {
            direction: sortItem.direction,
            systemGeneratedColumnType: sortItem.systemGeneratedColumnType,
          }
        }
        return null
      })
      .filter((sortInput): sortInput is NonNullable<typeof sortInput> => sortInput != null)

    // Create filter inputs for the filters saved on current tab (table-column and system-generated column filters)
    const filtersInput: TableColumnFilterInput[] = selectedTab.filters
      .map((filterItem) => {
        if (filterItem.tableColumn != null) {
          return {
            tableColumnId: filterItem.tableColumn.id,
            operator: filterItem.operator,
            operand: filterItem.operand ?? { data: null },
          }
        }
        if (filterItem.systemGeneratedColumnType != null) {
          return {
            systemGeneratedColumnType: filterItem.systemGeneratedColumnType,
            operator: filterItem.operator,
            operand: filterItem.operand ?? { data: null },
          }
        }
        return null
      })
      .filter((filterInput): filterInput is NonNullable<typeof filterInput> => filterInput != null)
    await createTableTab({
      variables: {
        input: {
          name: newName,
          tableId,
          booleanOperator: selectedTab.booleanOperator,
          columns: columnInputs,
          sorts: sortsInput,
          filters: filtersInput,
        },
      },
    })
    snackbar.showInfo(t('table_tab.duplicate.success', { sourceTabName: selectedTab.name }))
    handleTabMenuClose()
  }

  const handleSelectTabToDelete = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setTabToDelete(selectedTab)
  }

  const handleSelectTabToUpdate = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setTabToUpdate(selectedTab)
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        disableRestoreFocus
        onClose={handleTabMenuClose}
        open={!!anchorEl}
      >
        <MenuItem onClick={handleSelectTabToUpdate}>
          <Stack
            alignItems="center"
            direction="row"
            display="flex"
            justifyContent="center"
            spacing={1}
          >
            <RxPencil2 size={12} />
            <Typography variant="body1">{t('table_tab.menu.rename')}</Typography>
          </Stack>
        </MenuItem>
        <MenuItem>
          <Stack
            alignItems="center"
            direction="row"
            display="flex"
            justifyContent="flex-start"
            onClick={handleDuplicateTableTabClick}
            spacing={1}
          >
            <IoDuplicateOutline size={12} />
            <Typography variant="body1">{t('table_tab.menu.duplicate')}</Typography>
          </Stack>
        </MenuItem>
        {orderedTableTabs.length > 1 && (
          <MenuItem onClick={handleSelectTabToDelete}>
            <Stack
              alignItems="center"
              direction="row"
              display="flex"
              justifyContent="flex-start"
              spacing={1}
            >
              <RxTrash size={12} />
              <Typography variant="body1">{t('table_tab.menu.delete')}</Typography>
            </Stack>
          </MenuItem>
        )}
      </Menu>
      {tabToDelete && (
        <DeleteTableTabDialog
          handleTabChange={handleTabChange}
          handleTabMenuClose={handleTabMenuClose}
          orderedTableTabs={orderedTableTabs}
          setTabToDelete={setTabToDelete}
          tabToDelete={tabToDelete}
          tableName={tableName}
        />
      )}
      {tabToUpdate && (
        <RenameTableTabDialog
          handleTabMenuClose={handleTabMenuClose}
          orderedTableTabs={orderedTableTabs}
          setTabToUpdate={setTabToUpdate}
          tabToUpdate={tabToUpdate}
          tableName={tableName}
        />
      )}
    </>
  )
}
